import {EditorState, type ChangeSpec, type Extension} from '@codemirror/state';

/**
 * CodeMirror 6 extension that ensures the editor can only contain a single line of text.
 * Referenced from: https://discuss.codemirror.net/t/codemirror-6-single-line-and-or-avoid-carriage-return/2979/7
 */
export function singleLineMode(): Extension {
  const newlineRegex = /[\r\n\u0085\u2028\u2029]/;
  return EditorState.transactionFilter.of((tr) => {
    if (tr.changes.empty || !tr.docChanged) {
      return tr;
    }

    if (tr.newDoc.lines === 1) {
      return tr; // Single line change -> allow the transaction
    }

    if (tr.isUserEvent('input.paste')) {
      const removedNewlines: ChangeSpec[] = [];

      tr.changes.iterChanges((_fromA, _toA, fromB, _toB, ins) => {
        if (ins.lines <= 1) {
          return;
        }

        const lineIter = ins.iterLines().next();
        let index = fromB;

        while (!lineIter.done) {
          const lineText = lineIter.value;
          const start = index + lineText.length;
          if (
            start < tr.newDoc.length &&
            newlineRegex.test(tr.newDoc.sliceString(start, start + 1))
          ) {
            // By excluding the `insert` field in the ChangeSpec, the leading newline range will be deleted.
            removedNewlines.push({from: start, to: start + 1});
          }
          index = start + 1; // Skip the newline character on the next iteration
          lineIter.next();
        }
      });

      return [tr, {changes: removedNewlines, sequential: true}];
    }

    return []; // Non-paste event -> don't allow the transaction
  });
}
